import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { apiClient } from '../../../api/axios';
import { PaginationDefaults } from '../../../constants/defaults';

const fetchDeliveryAttempts = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { data } = await apiClient.get(`/delivery-attempts`, { params });
  return data;
};

export const useGetDeliveryLogsHandler = ({ searchQuery = {}, orgId }) => {
  const LIMIT = PaginationDefaults.REQUESTS_LIMIT;

  const dispatch = useDispatch();
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const queryClient = useQueryClient();

  const queryParams = {
    org_id: orgId,
    ...searchQuery,
    limit: LIMIT,
    offset: offset,
  };

  const {
    data: fetchedData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    {
      queryKey: ['deliveryAttempts', queryParams],
      queryFn: fetchDeliveryAttempts,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: 'always',
      enabled: !!orgId,
    },
    queryClient,
  );

  // Handle data updates for both initial fetch and load more
  useEffect(() => {
    if (!fetchedData) return;

    if (offset === 0) {
      // Initial fetch or refetch - replace all delivery logs
      setDeliveryLogs(fetchedData.attempts || []);
      setHasMore(fetchedData.has_more || false);
    } else {
      // Load more - append to existing delivery logs
      setDeliveryLogs((prevDeliveryLogs) => [...prevDeliveryLogs, ...(fetchedData.attempts || [])]);
      setHasMore(fetchedData.has_more || false);
      setIsLoadingMore(false);
    }
  }, [fetchedData, offset]);

  useEffect(() => {
    if (isError) {
      setIsLoadingMore(false);

      if (error instanceof AxiosError) {
        if (error.status) {
          dispatch(showSnackbar({ message: error.response.data.message, severity: 'error' }));
        } else {
          dispatch(showSnackbar({ message: error.message, severity: 'error' }));
        }
      } else {
        dispatch(showSnackbar({ message: JSON.stringify(error), severity: 'error' }));
      }
    }
  }, [dispatch, error, isError]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;

    setIsLoadingMore(true);
    setOffset((prevOffset) => prevOffset + LIMIT);
  }, [isLoadingMore, hasMore, isLoading, LIMIT]);

  const reset = useCallback(() => {
    setOffset(0);
    setDeliveryLogs([]);
    setHasMore(false);
    setIsLoadingMore(false);
    queryClient.invalidateQueries(['deliveryAttempts']);
  }, [queryClient]);

  // Track previous query to avoid resetting on initial mount
  const query = JSON.stringify(searchQuery);
  const prevQueryRef = useRef(query);

  useEffect(() => {
    if (prevQueryRef.current !== query) {
      setOffset(0);
      setDeliveryLogs([]);
      prevQueryRef.current = query;
    }
  }, [query]);

  return {
    deliveryLogs,
    hasMore,
    isLoading: isLoading && offset === 0,
    isLoadingMore,
    loadMore,
    reset,
  };
};
